import { Children, ElementType, FC, ReactNode, forwardRef, isValidElement } from "react";

import { Box, ThemeUIStyleObject } from "@theme/components/ThemeUi";

type Direction = "vertical" | "horizontal" | "vertical-reverse" | "horizontal-reverse";
type FlexDirection = "column" | "row" | "column-reverse" | "row-reverse";

export interface StackProps {
  /** Stack's direction */
  direction: Direction | Direction[];
  /** @default 4 */
  space?: number | string | Array<number | string>;
  /**
   * Whether Stack would take full width
   * @default false
   */
  full?: boolean;
  /** Flex align-center value */
  align?: string | string[];
  /** Flex justify-content value */
  justify?: string | string[];
  children: ReactNode;
  sx?: ThemeUIStyleObject;
  className?: string;
  p?: number | string | Array<number | string>;
  px?: number | string | Array<number | string>;
  py?: number | string | Array<number | string>;
  pt?: number | string | Array<number | string>;
  pl?: number | string | Array<number | string>;
  pb?: number | string | Array<number | string>;
  pr?: number | string | Array<number | string>;
  as?: ElementType;
}

const flexDirectionFromDirection = {
  vertical: "column",
  "vertical-reverse": "column-reverse",
  horizontal: "row",
  "horizontal-reverse": "row-reverse",
};

export const Stack: FC<StackProps> = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction,
      space = "s400",
      full = false,
      align,
      justify,
      children,
      as = "div",
      sx,
      className,
      ...rest
    },
    ref,
  ) => {
    const validChildrenArray = Children.toArray(children).filter(isValidElement);

    const flexDirection =
      typeof direction === "string"
        ? (flexDirectionFromDirection[direction] as FlexDirection)
        : (direction.map((d) => flexDirectionFromDirection[d]) as FlexDirection[]);

    const directionArray = direction as Direction[];
    if (typeof direction !== "string" && direction.length < 4) {
      const lastDirection = direction[direction.length - 1];
      const times = 4 - direction.length;
      for (let i = 0; i < times; i++) {
        directionArray.push(lastDirection);
      }
    }

    return (
      <Box
        ref={ref}
        as={as}
        sx={{
          display: "flex",
          flexDirection,
          flex: full ? 1 : "0 1 auto",
          alignItems: align,
          justifyContent: justify,
          ...sx,
        }}
        className={className}
        {...rest}
      >
        {validChildrenArray.map((child, index) => {
          const isLastChild = validChildrenArray.length === index + 1;

          const getSpacingProps = (d: Direction) =>
            d === "vertical"
              ? { mb: isLastChild ? 0 : space }
              : d === "vertical-reverse"
              ? { mt: isLastChild ? 0 : space }
              : d === "horizontal"
              ? { mr: isLastChild ? 0 : space }
              : { ml: isLastChild ? 0 : space };

          let spacingProps;

          if (typeof direction === "string") {
            spacingProps = getSpacingProps(direction);
          } else {
            spacingProps = directionArray
              .map((d) => getSpacingProps(d))
              .reduce(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (a: any, v: any, index: number) => {
                  a.mt[index] = v.mt ? (typeof v.mt === "object" ? v.mt[index] : v.mt) : 0;
                  a.mr[index] = v.mr ? (typeof v.mr === "object" ? v.mr[index] : v.mr) : 0;
                  a.mb[index] = v.mb ? (typeof v.mb === "object" ? v.mb[index] : v.mb) : 0;
                  a.ml[index] = v.ml ? (typeof v.ml === "object" ? v.ml[index] : v.ml) : 0;
                  return a;
                },
                { mt: [], mr: [], mb: [], ml: [] },
              );
          }

          return (
            <Box
              key={`stack-box-wrapper-${index}`}
              as={as === "ul" ? "li" : "div"}
              sx={{
                display: "inline-flex",
                flex: full ? 1 : "0 1 auto",
                justifyContent: "center",
                alignItems: "center",
                "& > *": { flex: 1 },
                ...spacingProps,
              }}
            >
              {child}
            </Box>
          );
        })}
      </Box>
    );
  },
);
